import { useState, useEffect, useRef } from 'react'
import { useParams, Link, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { T, MODE_LABELS } from '../lib/tokens'
import { useItem, useUpdateItemStatus, useUpdateItem, useItemChats, useCategories, useChatModels } from '../lib/hooks'
import { itemChat, itemDeepDive, modelLabel as apiModelLabel } from '../lib/api'
import { supabase } from '../lib/supabase'
import { Sidebar } from '../components/Sidebar'
import { Topbar } from '../components/Topbar'
import { GeminiDrawer, useGeminiDrawer } from '../components/GeminiDrawer'
import { CatBadge, Confidence, TimeAgo, LoadingSpinner, ErrorMessage, Thumb, MarkdownLite, CopyButton, FavoriteStar } from '../components/ui'

export function ItemDetailPage() {
  const { itemId } = useParams({ strict: false }) as { itemId: string }
  const { data: item, isLoading, error } = useItem(itemId)
  const { mutate: updateStatus } = useUpdateItemStatus()
  const { mutate: updateItem } = useUpdateItem()
  const { open: geminiOpen, setOpen: setGeminiOpen } = useGeminiDrawer()
  const navigate = useNavigate()

  function handleArchive() {
    updateStatus({ id: itemId, status: 'archived' }, { onSuccess: () => navigate({ to: '/' }) })
  }
  function handleTrash() {
    if (!confirm('ゴミ箱に移しますか？')) return
    updateStatus({ id: itemId, status: 'trashed' }, { onSuccess: () => navigate({ to: '/' }) })
  }

  return (
    <div style={{ display:'flex',height:'100dvh',background:T.bg,color:T.text,fontFamily:T.font,overflow:'hidden' }}>
      <Sidebar />
      <div style={{ flex:1,display:'flex',flexDirection:'column',overflow:'hidden' }}>
        <Topbar onGemini={() => setGeminiOpen(true)} />
        <main style={{ flex:1,overflowY:'auto',padding:'28px 32px',maxWidth:860 }}>

          {/* パンくず */}
          <div style={{ fontSize:11,fontFamily:T.fontMono,color:T.textMute,marginBottom:20,display:'flex',alignItems:'center',gap:6 }}>
            <Link to="/" style={{ color:T.textMute,textDecoration:'none' }}>memolake</Link>
            <span>/</span><span style={{ color:T.textDim }}>item</span>
          </div>

          {isLoading && <LoadingSpinner />}
          {error && <ErrorMessage message="アイテムの取得に失敗しました" />}

          {item && (
            <div style={{ display:'flex',flexDirection:'column',gap:24 }}>

              {/* タイトルとアクション */}
              <div style={{ display:'flex',alignItems:'flex-start',gap:12 }}>
                <div style={{ flex:1 }}>
                  <EditableTitle
                    itemId={itemId}
                    value={item.title ?? item.original_content.slice(0, 80)}
                  />
                  <div style={{ display:'flex',alignItems:'center',gap:8,flexWrap:'wrap' }}>
                    <EditableCategory itemId={itemId} catId={item.cat_id} />
                    <FavoriteStar
                      active={!!item.is_favorite}
                      size={14}
                      onToggle={()=>updateItem({ id:itemId, patch:{ is_favorite: !item.is_favorite } as any })}
                    />
                    <Confidence value={item.confidence} />
                    <span title="クリックで評価（同じ星で解除）" style={{ fontSize:14,letterSpacing:1,whiteSpace:'nowrap' }}>
                      {[1,2,3,4,5].map(n=>(
                        <span key={n}
                          onClick={()=>updateItem({ id:itemId, patch:{ rating: item.rating===n ? null : n } as any })}
                          style={{ cursor:'pointer', color:(item.rating??0)>=n ? T.accent : T.textMute }}
                        >★</span>
                      ))}
                    </span>
                    <span style={{ fontSize:10,fontFamily:T.fontMono,color:T.textMute }}>{MODE_LABELS[item.process_mode]??item.process_mode}</span>
                    <TimeAgo iso={item.created_at} />
                    {item.source_url&&<a href={item.source_url} target="_blank" rel="noreferrer" style={{ fontSize:11,fontFamily:T.fontMono,color:T.accent,textDecoration:'none' }}>↗ {(()=>{try{return new URL(item.source_url).hostname}catch{return item.source_url}})()}</a>}
                  </div>
                </div>
                {/* アクションボタン */}
                <div style={{ display:'flex',gap:6,flexShrink:0,marginTop:4 }}>
                  <ActionBtn label="アーカイブ" onClick={handleArchive} />
                  <ActionBtn label="削除" danger onClick={handleTrash} />
                </div>
              </div>

              {/* 大エリア（link=画像 / note=箇条書き / image=添付） */}
              <Thumb
                catId={item.cat_id}
                title={item.title ?? ''}
                kind={item.kind}
                ratio="16/6"
                imageUrl={item.og_image_url ?? item.image_url}
                summary={item.summary ?? item.original_content}
              />

              {/* 元のメモ（編集可） */}
              <EditableMemo itemId={itemId} value={item.original_content} />

              {/* Gemini 要約 */}
              {item.summary && (
                <Section icon="✦" label="Gemini 要約" accent>
                  <div style={{ fontSize:13,color:T.text,lineHeight:1.8 }}>{item.summary}</div>
                </Section>
              )}

              {/* Gemini 追記 */}
              {item.gemini_note && (
                <Section icon="✦" label={item.gemini_note_searched ? 'Gemini 追記（🔍検索あり）' : 'Gemini 追記'} accent>
                  <div style={{ fontSize:13,color:T.textDim,lineHeight:1.8,whiteSpace:'pre-wrap' }}>{item.gemini_note}</div>
                </Section>
              )}

              {/* 深掘り（本文取得＋解析。結果は上の「Gemini 追記」に表示される） */}
              <DeepDiveSection itemId={itemId} sourceUrl={item.source_url} hasNote={!!item.gemini_note} />

              {/* 書き直し */}
              {item.rewritten_text && (
                <Section icon="✏️" label="Gemini 書き直し" accent>
                  <div style={{ fontSize:13,color:T.textDim,lineHeight:1.8,whiteSpace:'pre-wrap' }}>{item.rewritten_text}</div>
                </Section>
              )}

              {/* このメモについて Gemini に質問 */}
              <ItemChat itemId={itemId} />
            </div>
          )}
        </main>
      </div>

      <GeminiDrawer open={geminiOpen} onClose={() => setGeminiOpen(false)} />
    </div>
  )
}

// 深掘り（本文取得＋解析）。失敗時は原因を提示し、手動で「再取得」か「要約＋検索」を選ばせる。
function DeepDiveSection({ itemId, sourceUrl, hasNote }: { itemId: string; sourceUrl: string | null; hasNote: boolean }) {
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [fallback, setFallback] = useState(false)

  async function run(mode: 'body' | 'research') {
    setBusy(true)
    setMsg(mode === 'research' ? '🔍 要約＋検索で深掘り中…' : '🔍 深掘り中…（本文取得＋解析）')
    try {
      const r = await itemDeepDive(itemId, mode)
      if (r.ok) {
        setFallback(false); setMsg(`📖 深掘り完了（${r.searched ? '🔍 ウェブ検索あり' : '📄 本文のみ'}）。「Gemini 追記」に反映しました。`)
        qc.invalidateQueries({ queryKey: ['item', itemId] })
      } else if (r.reason === 'no_source') {
        setMsg('このメモにはURLが無いため本文の深掘りはできません。🔍要約＋検索なら可能です。')
        setFallback(true)
      } else if (r.reason === 'no_body') {
        setFallback(true)
        const causeMsg: Record<string, string> = {
          auth: 'ログイン必須（認証が必要）の可能性',
          blocked: '取得ブロック（bot対策の可能性）',
          notfound: 'ページが見つかりません（リンク切れの可能性）',
          server: 'サイト側エラー（一時的な可能性）',
          network: '接続できませんでした（リンク切れ・サイト停止・一時障害の可能性）',
          redirect: '転送が多すぎます（リダイレクトループ・Cookie必須の可能性）',
          js: '本文が読めません（JavaScript描画・購読制の可能性）',
        }
        const temporary = r.cause === 'server' || r.cause === 'network'
        // ブロック/認証ウォール/同意ゲート(リダイレクト自己ループ)は再取得しても本文は取れない。
        // 要約のみ表示し、原文は直接開いてもらう。
        const blocked = r.cause === 'blocked' || r.cause === 'auth' || r.cause === 'redirect'
        const tip = temporary ? '📖再取得で再試行できます。'
                  : blocked   ? '要約のみ表示します。原文はリンクから直接ご覧ください。'
                  : '🔍要約＋検索が向いています。'
        setMsg(`本文を取得できませんでした：${causeMsg[r.cause ?? ''] ?? '本文を取得できません'}${r.status ? `（HTTP ${r.status}）` : ''}。${tip}`)
      } else {
        setMsg('解析に失敗しました（再試行できます）')
      }
    } catch {
      setMsg('深掘りに失敗しました')
    } finally {
      setBusy(false)
    }
  }

  const hasUrl = !!sourceUrl
  return (
    <Section icon="📖" label={hasNote ? '深掘り（再実行）' : '深掘り'}>
      <div style={{ display:'flex',gap:8,flexWrap:'wrap',alignItems:'center' }}>
        {hasUrl && (
          <button onClick={() => run('body')} disabled={busy}
            style={{ padding:'6px 14px',borderRadius:6,border:`1px solid ${T.borderHi}`,background:T.bgActive,color:T.text,fontSize:12,cursor:busy?'default':'pointer',fontFamily:T.font }}>
            {busy ? '🔍…' : (fallback ? '📖 本文を再取得' : (hasNote ? '📖 もう一度深掘り' : '📖 深掘り'))}
          </button>
        )}
        {(fallback || !hasUrl) && (
          <button onClick={() => run('research')} disabled={busy}
            style={{ padding:'6px 14px',borderRadius:6,border:`1px solid ${T.border}`,background:'transparent',color:T.textDim,fontSize:12,cursor:busy?'default':'pointer',fontFamily:T.font }}>
            🔍 要約＋検索で深掘り
          </button>
        )}
        {fallback && hasUrl && (
          <a href={sourceUrl!} target="_blank" rel="noopener noreferrer"
            style={{ padding:'6px 14px',borderRadius:6,border:`1px solid ${T.border}`,background:'transparent',color:T.textDim,fontSize:12,textDecoration:'none',fontFamily:T.font }}>
            🔗 原文を開く
          </a>
        )}
      </div>
      {msg && <div style={{ fontSize:11,color:T.textDim,marginTop:8 }}>{msg}</div>}
    </Section>
  )
}

function Section({ icon,label,children,accent }: { icon:string;label:string;children:React.ReactNode;accent?:boolean }) {
  return (
    <div style={{ background:T.bgRaised,border:`1px solid ${accent?T.accentBorder:T.border}`,borderRadius:8,overflow:'hidden' }}>
      <div style={{ padding:'10px 16px',borderBottom:`1px solid ${accent?T.accentBorder:T.border}`,background:accent?T.accentSoft:'transparent',display:'flex',alignItems:'center',gap:7,fontSize:11,fontFamily:T.fontMono,color:accent?'oklch(0.85 0.12 258)':T.textMute,letterSpacing:'0.04em' }}>
        <span>{icon}</span><span>{label}</span>
      </div>
      <div style={{ padding:'14px 16px' }}>{children}</div>
    </div>
  )
}

function ActionBtn({ label,onClick,danger }: { label:string;onClick:()=>void;danger?:boolean }) {
  return (
    <button onClick={onClick} style={{ padding:'6px 12px',borderRadius:5,fontSize:12,cursor:'pointer',background:'transparent',fontFamily:'inherit',border:`1px solid ${danger?T.warn+'60':T.border}`,color:danger?T.warn:T.textDim,transition:'all 0.12s' }}
      onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.background=danger?T.warnSoft:T.bgHover}}
      onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.background='transparent'}}
    >{label}</button>
  )
}

// ── 編集可能なタイトル ────────────────────────────────────
function EditableTitle({ itemId,value }: { itemId:string;value:string }) {
  const { mutate:update,isPending } = useUpdateItem()
  const [editing,setEditing] = useState(false)
  const [draft,setDraft]     = useState(value)

  useEffect(() => { if(!editing) setDraft(value) }, [value,editing])

  function save() {
    const next = draft.trim()
    if (!next || next === value) { setEditing(false); return }
    update({ id:itemId,patch:{ title:next } }, { onSuccess:()=>setEditing(false) })
  }

  if (!editing) {
    return (
      <div style={{ display:'flex',alignItems:'flex-start',gap:8,margin:'0 0 12px' }}>
        <h1 style={{ fontSize:22,fontWeight:700,color:T.text,lineHeight:1.3,margin:0,flex:1 }}>{value}</h1>
        <button
          onClick={()=>setEditing(true)}
          title="タイトルを編集"
          style={{ flexShrink:0,marginTop:4,background:'transparent',border:'none',color:T.accent,cursor:'pointer',fontSize:11,fontFamily:T.fontMono,padding:0 }}
        >✎ 編集</button>
      </div>
    )
  }
  return (
    <div style={{ margin:'0 0 12px' }}>
      <input
        value={draft}
        autoFocus
        onChange={e=>setDraft(e.target.value)}
        onKeyDown={e=>{ if(e.key==='Enter') save(); if(e.key==='Escape') setEditing(false) }}
        style={{ width:'100%',fontSize:22,fontWeight:700,color:T.text,lineHeight:1.3,background:T.bgRaised,border:`1px solid ${T.borderHi}`,borderRadius:6,padding:'6px 10px',fontFamily:'inherit',outline:'none',boxSizing:'border-box' }}
      />
      <div style={{ display:'flex',gap:6,marginTop:8,alignItems:'center' }}>
        <ActionBtn label={isPending?'保存中…':'保存'} onClick={save} />
        <ActionBtn label="キャンセル" onClick={()=>setEditing(false)} />
        <span style={{ fontSize:10,fontFamily:T.fontMono,color:T.textMute,marginLeft:'auto' }}>Enter で保存・Esc で取消</span>
      </div>
    </div>
  )
}

// ── 編集可能な元メモ ──────────────────────────────────────
function EditableMemo({ itemId,value }: { itemId:string;value:string }) {
  const { mutate:update,isPending } = useUpdateItem()
  const [editing,setEditing] = useState(false)
  const [draft,setDraft]     = useState(value)

  useEffect(() => { if(!editing) setDraft(value) }, [value,editing])

  function save() {
    const next = draft.trim()
    if (!next || next === value) { setEditing(false); return }
    update({ id:itemId,patch:{ original_content:next } }, { onSuccess:()=>setEditing(false) })
  }

  return (
    <div style={{ background:T.bgRaised,border:`1px solid ${T.border}`,borderRadius:8,overflow:'hidden' }}>
      <div style={{ padding:'10px 16px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:7,fontSize:11,fontFamily:T.fontMono,color:T.textMute,letterSpacing:'0.04em' }}>
        <span>▪</span><span style={{ flex:1 }}>元のメモ（原文）</span>
        {!editing && <button onClick={()=>setEditing(true)} style={{ background:'transparent',border:'none',color:T.accent,cursor:'pointer',fontSize:11,fontFamily:T.fontMono,padding:0 }}>✎ 編集</button>}
      </div>
      <div style={{ padding:'14px 16px' }}>
        {editing ? (
          <>
            <textarea
              value={draft}
              autoFocus
              onChange={e=>setDraft(e.target.value)}
              onKeyDown={e=>{ if(e.key==='Escape') setEditing(false); if((e.metaKey||e.ctrlKey)&&e.key==='Enter') save() }}
              style={{ width:'100%',minHeight:140,fontSize:13,color:T.text,lineHeight:1.7,background:T.bg,border:`1px solid ${T.borderHi}`,borderRadius:6,padding:'10px 12px',fontFamily:'inherit',outline:'none',resize:'vertical',boxSizing:'border-box' }}
            />
            <div style={{ display:'flex',gap:6,marginTop:8,alignItems:'center' }}>
              <ActionBtn label={isPending?'保存中…':'保存'} onClick={save} />
              <ActionBtn label="キャンセル" onClick={()=>setEditing(false)} />
              <span style={{ fontSize:10,fontFamily:T.fontMono,color:T.textMute,marginLeft:'auto' }}>⌘+Enter で保存・Esc で取消</span>
            </div>
          </>
        ) : (
          <div style={{ fontSize:13,color:T.textDim,lineHeight:1.7,whiteSpace:'pre-wrap',wordBreak:'break-word' }}>
            {value}
          </div>
        )}
      </div>
    </div>
  )
}

// ── カテゴリ変更（クリックでドロップダウン） ─────────────────
function EditableCategory({ itemId, catId }: { itemId: string; catId: string | null }) {
  const { data: categories = [] } = useCategories()
  const { mutate: updateItem } = useUpdateItem()
  const [open, setOpen] = useState(false)

  function select(id: string) {
    updateItem({ id: itemId, patch: { cat_id: id } as any })
    setOpen(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        title="カテゴリを変更"
        style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        <CatBadge catId={catId} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9 }} />
          <div style={{
            position: 'absolute', top: '100%', left: 0, zIndex: 10,
            background: T.bgRaised, border: `1px solid ${T.borderHi}`, borderRadius: 7,
            padding: 6, display: 'flex', flexDirection: 'column', gap: 2,
            minWidth: 140, marginTop: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          }}>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => select(cat.id)} style={{
                background: cat.id === catId ? T.bgActive : 'transparent',
                border: 'none', borderRadius: 5, padding: '5px 10px',
                color: cat.id === catId ? T.text : T.textDim,
                fontSize: 12, cursor: 'pointer', textAlign: 'left', fontFamily: T.font,
              }}>{cat.label}</button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ── アイテム内 Gemini チャット ────────────────────────────
interface ChatMsg { role:'user'|'assistant';content:string;searched?:boolean;attempt?:number;model?:string|null;ts:string }

function modelLabel(model?: string | null): string {
  if (!model) return ''
  return apiModelLabel(model)
}

// 回答の検索/試行状況ラベル（空文字＝最初から検索せず＝何も出さない）。
//   🔍検索あり        検索して回答
//   🔍検索あり(再試行)  1回目空→検索で回答
//   （空）            最初から検索せず
//   検索なし(Fail)     検索が空でフォールバック
function chatStatusLabel(searched?: boolean, attempt?: number): string {
  const a = attempt ?? 1
  if (searched) return a > 1 ? '🔍検索あり(再試行)' : '🔍検索あり'
  if (a >= 3) return '検索なし(Fail)'
  return ''
}

function fmtTime(iso:string):string {
  return new Date(iso).toLocaleString('ja-JP',{ month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit' })
}

function ItemChat({ itemId }: { itemId:string }) {
  const { data:saved } = useItemChats(itemId)
  const [messages,setMessages] = useState<ChatMsg[]>([])
  const [seeded,setSeeded]     = useState(false)
  const [input,setInput]       = useState('')
  const [loading,setLoading]   = useState(false)
  // 選択肢は Worker から取得する。未選択（null）の間は一覧の先頭を使う。
  // 一覧を取得できないときは model が空になり、モデル指定なしで送る（Worker の既定で回答）。
  const { data:chatModelList, isError:chatModelsError } = useChatModels()
  const chatModels             = chatModelList?.models   // メモ詳細は Gemini のみ（Claude はメモを参照しない経路）
  const [picked,setPicked]     = useState<string | null>(null)
  const model                  = picked ?? chatModels?.[0]?.value ?? ''
  const [useMemos,setUseMemos] = useState(false)
  const [failedQ,setFailedQ]   = useState<string | null>(null)  // 空応答だった直近の質問（ワンタップ再送用）
  const bottomRef              = useRef<HTMLDivElement>(null)

  // 保存済みの会話を初回だけ読み込んで表示に反映
  useEffect(() => {
    if (!seeded && saved) {
      setMessages(saved.map(r=>({ role:r.role,content:r.content,searched:r.searched,model:r.model,ts:r.created_at })))
      setSeeded(true)
    }
  }, [saved,seeded])

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }) }, [messages])

  // 質問を実行してアシスタント応答を反映する（履歴は呼び出し側が用意）。
  // 空応答（回答を生成できませんでした）は失敗扱いにし、再タイプ不要で再送できるよう failedQ に退避する。
  async function runQuery(q:string, history:{ role:'user'|'assistant'; content:string }[]) {
    setLoading(true)
    try {
      const { answer, searched, attempt, model: usedModel } = await itemChat(itemId, q, history, model || undefined, useMemos)
      const savedModel = usedModel ?? (model || null)   // Worker が返した実モデル名。無ければ選択キー
      if (!answer || answer === '（回答を生成できませんでした）') {
        setFailedQ(q)   // 失敗 → 「もう一度送信」ボタンを表示（吹き出しは追加しない）
      } else {
        setFailedQ(null)
        setMessages(prev=>[...prev,{ role:'assistant',content:answer,searched,attempt,model:savedModel,ts:new Date().toISOString() }])
        supabase.from('ml_item_chats').insert({ item_id:itemId,role:'assistant',content:answer,searched:!!searched,model:savedModel }).then(()=>{})
      }
    } catch {
      setFailedQ(q)   // 通信エラーも同じく再送可能に
    } finally {
      setLoading(false)
    }
  }

  async function send(text:string) {
    const q = text.trim()
    if (!q || loading) return
    const history = messages.slice(-200).map(({ role,content })=>({ role,content }))
    setMessages(prev=>[...prev,{ role:'user',content:q,ts:new Date().toISOString() }])
    setInput('')
    // ユーザー発言を保存（失敗してもチャットは継続）
    supabase.from('ml_item_chats').insert({ item_id:itemId,role:'user',content:q }).then(()=>{})
    await runQuery(q, history)
  }

  // ワンタップ再送: 直近の質問を、ユーザー吹き出しを増やさずに再実行する。
  async function resend() {
    if (!failedQ || loading) return
    const q = failedQ
    setFailedQ(null)
    // 末尾のユーザー発言が対象。それを除いた履歴を渡す。
    const history = messages.slice(0,-1).slice(-200).map(({ role,content })=>({ role,content }))
    await runQuery(q, history)
  }

  async function reset() {
    setMessages([])
    await supabase.from('ml_item_chats').delete().eq('item_id', itemId)
  }

  return (
    <Section icon="✦" label="このメモにだけ質問（Gemini）" accent>
      <div style={{ display:'flex',flexDirection:'column',gap:12 }}>
        {messages.length===0 && (
          <div style={{ fontSize:12,color:T.textDim,lineHeight:1.7 }}>
            <strong style={{ color:T.textDim }}>このメモ1件だけ</strong>を対象に質問できます（全メモ横断は右上の ⌘J）。例:「3行で要約して」「関連する論点は？」「英語にして」
          </div>
        )}
        {messages.map((m,i)=>(
          <div key={i} style={{ display:'flex',flexDirection:'column',alignItems:m.role==='user'?'flex-end':'flex-start',gap:3 }}>
            <div style={{
              maxWidth:'88%',background:m.role==='user'?T.accentSoft:T.bgPanel,
              border:`1px solid ${m.role==='user'?T.accentBorder:T.border}`,
              borderRadius:8,padding:'9px 12px',fontSize:13,color:T.text,lineHeight:1.7,
              whiteSpace:m.role==='user'?'pre-wrap':'normal',wordBreak:'break-word',
            }}>
              {m.role==='assistant' ? <MarkdownLite text={m.content} /> : m.content}
            </div>
            <div style={{ display:'flex',alignItems:'center',gap:8,fontSize:10,fontFamily:T.fontMono,color:T.textMute,padding:'0 2px' }}>
              <span>{fmtTime(m.ts)}</span>
              {m.role==='assistant' && m.model && <span>{modelLabel(m.model)}</span>}
              {m.role==='assistant' && chatStatusLabel(m.searched, m.attempt) !== '' && <span>{chatStatusLabel(m.searched, m.attempt)}</span>}
              <CopyButton text={m.content} />
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ fontSize:12,color:T.textMute,fontFamily:T.fontMono }}>✦ 考え中…</div>
        )}
        {failedQ && !loading && (
          <div style={{ display:'flex',alignItems:'center',gap:10,fontSize:12,color:T.textDim,background:T.bgPanel,border:`1px solid ${T.border}`,borderRadius:8,padding:'9px 12px' }}>
            <span>⚠️ 回答を生成できませんでした。</span>
            <button onClick={resend}
              style={{ marginLeft:'auto',padding:'4px 12px',borderRadius:6,border:`1px solid ${T.borderHi}`,background:T.bgActive,color:T.text,fontSize:12,cursor:'pointer',fontFamily:T.font }}>
              🔄 もう一度送信
            </button>
          </div>
        )}
        <div ref={bottomRef} />

        <div style={{ marginBottom:8 }}>
          <button
            onClick={()=>setUseMemos(v=>!v)}
            title="保存メモ全体（要約）も参照して回答する"
            style={{
              background: useMemos ? T.bgActive : 'transparent',
              border:`1px solid ${useMemos ? T.borderHi : T.border}`,
              color: useMemos ? T.text : T.textMute,
              borderRadius:5,padding:'3px 9px',fontSize:11,cursor:'pointer',fontFamily:T.fontMono,
            }}
          >{useMemos ? 'メモ全体を参照 ON' : 'メモ全体を参照 OFF'}</button>
        </div>
        <div style={{ display:'flex',alignItems:'center',gap:8,background:T.bg,border:`1px solid ${T.borderHi}`,borderRadius:6,padding:'8px 10px' }}>
          <select
            value={model}
            onChange={e=>setPicked(e.target.value || null)}
            title="モデルを選択"
            style={{ background:T.bgHover,border:`1px solid ${T.border}`,borderRadius:4,color:T.text,fontSize:11,fontFamily:T.fontMono,padding:'3px 5px',cursor:'pointer',outline:'none' }}
          >
            {!chatModels && <option value="">{chatModelsError ? '⚠️ モデル一覧を取得できません' : '読み込み中…'}</option>}
            {chatModels?.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <textarea
            value={input}
            onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){ e.preventDefault(); send(input) } }}
            placeholder="このメモについて質問…"
            rows={1}
            style={{ flex:1,background:'transparent',border:'none',outline:'none',color:T.text,fontSize:13,fontFamily:T.font,resize:'vertical',minHeight:20,maxHeight:240,lineHeight:1.6 }}
          />
          <button
            onClick={()=>send(input)}
            disabled={!input.trim()||loading}
            style={{ background:input.trim()?T.accent:T.bgHover,border:'none',borderRadius:4,color:input.trim()?'#0a0b0e':T.textMute,padding:'4px 10px',fontSize:11,fontWeight:600,cursor:input.trim()?'pointer':'default',fontFamily:T.fontMono }}
          >↵ ask</button>
        </div>
        {messages.length>0 && (
          <button onClick={reset} style={{ alignSelf:'flex-start',background:'transparent',border:'none',color:T.textMute,fontSize:11,cursor:'pointer',fontFamily:T.font,padding:0 }}>会話をリセット</button>
        )}
      </div>
    </Section>
  )
}
